class CustomHeroSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.custom-hero-slider__slide'));

    if (this.slides.length <= 1) return;

    this.current = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 6000;
    this.transitionSpeed = parseInt(this.dataset.transitionSpeed, 10) || 800;

    this.style.setProperty('--slider-transition', `${this.transitionSpeed}ms`);

    this.isAnimating = false;

    // pause on hover / focus
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());

    // swipe support
    this.addSwipeSupport();

    // mouse wheel / trackpad scroll support
    this.addWheelSupport();

    // pause when tab hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    });

    // Every video starts at preload="none" in the template - nothing is
    // fetched until we say so here. A slide can carry both a desktop and a
    // mobile video, but only one is ever shown at a time via CSS, so we
    // only ever touch the one that's actually visible on this breakpoint;
    // the other is never downloaded at all.
    this.activateSlideVideos(this.slides[this.current]);

    // Once the browser is idle, quietly prefetch (not play) the *visible-
    // breakpoint* video for every other slide, so swiping to a later video
    // slide doesn't have to wait on the network for it.
    const preloadOtherSlides = () => {
      this.slides.forEach((slide, i) => {
        if (i !== this.current) this.preloadSlideVideos(slide);
      });
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preloadOtherSlides, { timeout: 4000 });
    } else {
      setTimeout(preloadOtherSlides, 2000);
    }

    // If the viewport crosses the mobile/desktop breakpoint (e.g. rotating
    // a tablet), make sure the newly-relevant video is ready.
    const breakpointQuery = window.matchMedia('(max-width: 749px)');
    const handleBreakpointChange = () => {
      this.activateSlideVideos(this.slides[this.current]);
      preloadOtherSlides();
    };
    if (breakpointQuery.addEventListener) {
      breakpointQuery.addEventListener('change', handleBreakpointChange);
    }

    if (this.autoplay) this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  // True if el is actually laid out right now (not sitting inside a
  // display:none ancestor) - used to tell which of a slide's desktop/mobile
  // media wrappers is the one currently shown by CSS.
  isVisible(el) {
    return el.offsetParent !== null;
  }

  // Plays whichever video is visible on the current breakpoint, from the
  // start, and makes sure the other one (if present) is paused.
  activateSlideVideos(slideEl) {
    slideEl.querySelectorAll('video').forEach((video) => {
      if (this.isVisible(video)) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  // Background-only prefetch: fetches data for whichever video would
  // actually be shown on this breakpoint, without playing it and without
  // ever touching the hidden desktop/mobile counterpart.
  preloadSlideVideos(slideEl) {
    slideEl.querySelectorAll('video').forEach((video) => {
      if (this.isVisible(video) && video.readyState === 0) {
        video.load();
      }
    });
  }

  goTo(index, direction = 'next') {
    if (this.isAnimating) return;

    const total = this.slides.length;
    const next = (index + total) % total;
    if (next === this.current) return;

    this.isAnimating = true;

    const outgoing = this.slides[this.current];
    const incoming = this.slides[next];
    const directionClass = direction === 'prev' ? 'is-direction-prev' : 'is-direction-next';

    // The outgoing slide is left completely alone - it just stays put,
    // visible, until the incoming slide has fully covered it.
    // Stack the incoming slide above it and position it off-screen first
    // (no transition yet).
    incoming.style.zIndex = 3;
    incoming.classList.add('is-entering', directionClass);

    // Wait two animation frames instead of forcing a synchronous reflow
    // read (the old `void incoming.offsetHeight` line). By the second rAF
    // callback, the browser has painted the 'is-entering' state, so
    // removing it and adding 'is-active' here still triggers the CSS
    // transition correctly, without the forced synchronous layout.
    requestAnimationFrame(() => {
      this.activateSlideVideos(incoming);
      requestAnimationFrame(() => {
        incoming.classList.remove('is-entering', directionClass);
        incoming.classList.add('is-active');
      });
    });

    this.current = next;

    // Once the incoming slide has fully covered the outgoing one, hide the
    // outgoing slide and reset both slides' stacking so they're ready for
    // the next transition.
    clearTimeout(this._cleanupTimer);
    this._cleanupTimer = setTimeout(() => {
      outgoing.classList.remove('is-active');
      incoming.style.zIndex = '';
      this.isAnimating = false;
    }, this.transitionSpeed + 50);
  }

  startAutoplay() {
    if (!this.autoplay) return;
    this.stopAutoplay();
    this.timer = setInterval(() => this.goTo(this.current + 1, 'next'), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
  }

  restartAutoplay() {
    if (this.autoplay) this.startAutoplay();
  }

  addWheelSupport() {
    // How much accidental trackpad jitter to ignore before treating it as an intentional scroll
    const threshold = 8;

    this.addEventListener(
      'wheel',
      (e) => {
        // While a slide change is animating, swallow further scroll so the
        // page doesn't jump underneath the transition.
        if (this.isAnimating) {
          e.preventDefault();
          return;
        }

        if (Math.abs(e.deltaY) < threshold) return;

        const scrollingDown = e.deltaY > 0;
        const total = this.slides.length;
        const atLastSlide = this.current === total - 1;
        const atFirstSlide = this.current === 0;

        // At the last slide, let a further downward scroll continue past the
        // section onto the rest of the page (and same going up on the first slide).
        if (scrollingDown && atLastSlide) return;
        if (!scrollingDown && atFirstSlide) return;

        e.preventDefault();

        if (scrollingDown) {
          this.goTo(this.current + 1, 'next');
        } else {
          this.goTo(this.current - 1, 'prev');
        }
        this.restartAutoplay();
      },
      { passive: false }
    );
  }

  addSwipeSupport() {
    let startY = 0;
    let isDragging = false;
    let action = null; // 'consume' (we handle it, block native scroll) | 'release' (let the browser handle it)
    const threshold = 50; // px of movement needed to actually trigger a slide change
    const activation = 6; // px before we commit to consume/release for this gesture

    const isAtPageTop = () => (window.scrollY || document.documentElement.scrollTop || 0) <= 0;

    // Switches off the browser's native pull-to-refresh / rubber-band bounce
    // for the duration of a touch on the slider, restoring it the moment the
    // touch ends. This is on top of the preventDefault() calls below, not a
    // replacement for them - some browsers (notably Chrome's pull-to-refresh)
    // key off this CSS property directly rather than only off preventDefault.
    const lockPageScroll = () => {
      document.documentElement.style.overscrollBehaviorY = 'none';
      document.body.style.overscrollBehaviorY = 'none';
    };
    const unlockPageScroll = () => {
      document.documentElement.style.overscrollBehaviorY = '';
      document.body.style.overscrollBehaviorY = '';
    };

    this.addEventListener(
      'touchstart',
      (e) => {
        if (this.isAnimating) {
          isDragging = false;
          return;
        }
        startY = e.touches[0].clientY;
        isDragging = true;
        action = null;
        lockPageScroll();
      },
      { passive: true }
    );

    this.addEventListener(
      'touchmove',
      (e) => {
        if (!isDragging) return;
        const currentY = e.touches[0].clientY;
        const diff = startY - currentY; // positive = finger moving up (next), negative = finger moving down (prev)

        // Decide the gesture's fate the moment it's big enough to read a
        // direction, and commit to it for the rest of the gesture. Deciding
        // late (or re-checking every event) is what let the browser's native
        // scroll / pull-to-refresh take over before our preventDefault could
        // stop it.
        if (action === null) {
          // Chrome/Safari decide whether to start their own scroll gesture -
          // pull-to-refresh, rubber-banding, the address-bar show/hide
          // animation - on the very first touchmove frame, before we've
          // moved far enough to read a direction below. Waiting until we've
          // classified the gesture is too late, so claim every touch by
          // default while we're still deciding. The only time that's wrong
          // is a genuine 'release' gesture, and losing the first few px of
          // that to preventDefault is imperceptible.
          e.preventDefault();

          if (Math.abs(diff) < activation) return;

          const total = this.slides.length;
          if (diff > 0) {
            // swiping up -> next slide, unless we're already on the last one,
            // in which case let the page scroll down past the section.
            action = this.current === total - 1 ? 'release' : 'consume';
          } else {
            // swiping down -> previous slide, unless we're on the first slide.
            // If we're also at the very top of the page, releasing this to
            // the browser is exactly what triggers pull-to-refresh, so we
            // swallow it instead (it just becomes a harmless no-op).
            if (this.current === 0) {
              action = isAtPageTop() ? 'consume' : 'release';
            } else {
              action = 'consume';
            }
          }
        }

        if (action === 'consume') {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    this.addEventListener(
      'touchend',
      (e) => {
        unlockPageScroll();

        if (!isDragging) return;
        isDragging = false;

        if (this.isAnimating || action !== 'consume') {
          action = null;
          return;
        }

        const endY = e.changedTouches[0].clientY;
        const diff = startY - endY;

        if (diff > threshold && this.current < this.slides.length - 1) {
          this.goTo(this.current + 1, 'next');
          this.restartAutoplay();
        } else if (diff < -threshold && this.current > 0) {
          this.goTo(this.current - 1, 'prev');
          this.restartAutoplay();
        }

        action = null;
      },
      { passive: true }
    );

    // Safety net: if the gesture is interrupted (e.g. an incoming call, or
    // the OS taking over for its own gesture) touchend never fires, so make
    // sure the page scroll lock doesn't get stuck on.
    this.addEventListener(
      'touchcancel',
      () => {
        unlockPageScroll();
        isDragging = false;
        action = null;
      },
      { passive: true }
    );
  }
}

customElements.define('custom-hero-slider', CustomHeroSlider);

// Upgrade the plain <div> section wrapper into the custom element behavior
// without needing to change the tag in the liquid file.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.custom-hero-slider').forEach((el) => {
    if (!el.dataset.upgraded) {
      el.dataset.upgraded = 'true';
      Object.setPrototypeOf(el, CustomHeroSlider.prototype);
      el.connectedCallback();
    }
  });
});